/* ══════════════════ Jungle Camp Rewards — Accurate Per-Level Data ══════════════════ */

/**
 * XP arrays are indexed by camp level (1-8). Camps level up each time they're killed.
 * Gold values are fixed per kill.
 */

/* ── Raptors ── */
const CRIMSON_RAPTOR_XP = [0, 20, 20, 25, 26, 27, 28, 29, 30]; // index 0 unused
const CRIMSON_RAPTOR_GOLD = 35;
const SMALL_RAPTOR_XP = [0, 10, 10, 12.5, 13, 13.5, 14, 14.5, 15];
const SMALL_RAPTOR_GOLD = 8;
const SMALL_RAPTOR_COUNT = 5;

/* ── Wolves ── */
const GREATER_WOLF_XP = [0, 50, 50, 62.5, 65, 67.5, 70, 72.5, 75];
const GREATER_WOLF_GOLD = 55;
const SMALL_WOLF_XP = [0, 15, 15, 18.75, 19.5, 20.25, 21, 21.75, 22.5];
const SMALL_WOLF_GOLD = 15;
const SMALL_WOLF_COUNT = 2;

/* ── Krugs ── */
const ANCIENT_KRUG_XP = [0, 15, 15, 18.75, 19.5, 20.25, 21, 21.75, 22.5];
const ANCIENT_KRUG_GOLD = 15;
const KRUG_XP = [0, 10, 10, 12.5, 13, 13.5, 14, 14.5, 15];
const KRUG_GOLD = 10;
const MINI_KRUG_XP = [0, 16, 16, 20, 20.8, 21.6, 22.4, 23.2, 24];
const MINI_KRUG_GOLD = 14;
const MINI_KRUG_COUNT = 6; // 4 from ancient + 2 from normal

/* ── Gromp ── */
const GROMP_XP = [0, 120, 120, 150, 156, 162, 168, 174, 180];
const GROMP_GOLD = 80;

/* ── Blue Buff ── */
const BLUE_BUFF_XP = [0, 95, 95, 118.75, 123.5, 128.25, 133, 137.75, 142.5];
const BLUE_BUFF_GOLD = 90;

/* ── Red Buff ── */
const RED_BUFF_XP = [0, 95, 95, 118.75, 123.5, 128.25, 133, 137.75, 142.5];
const RED_BUFF_GOLD = 90;

/* ── Scuttle Crab ── */
const SCUTTLE_XP = [0, 80, 80, 100, 104, 108, 112, 116, 120];
const SCUTTLE_GOLD = 70;

/* ══════════════════ Public API ══════════════════ */

export interface CampRewards {
  gold: number;
  xp: number;
  cs: number;
}

function clampLevel(level: number): number {
  return Math.max(1, Math.min(8, level));
}

/**
 * Get total rewards for clearing a camp at a given level.
 * Camp type is extracted from the camp ID (e.g. "red-blue_buff" → "blue_buff").
 */
export function getCampRewards(campId: string, campLevel: number): CampRewards {
  const type = extractCampType(campId);
  const lvl = clampLevel(campLevel);

  switch (type) {
    case 'raptors':
      return {
        gold: CRIMSON_RAPTOR_GOLD + SMALL_RAPTOR_COUNT * SMALL_RAPTOR_GOLD,
        xp: Math.round(CRIMSON_RAPTOR_XP[lvl] + SMALL_RAPTOR_COUNT * SMALL_RAPTOR_XP[lvl]),
        cs: 2 + SMALL_RAPTOR_COUNT * 0.4, // 4
      };
    case 'wolves':
      return {
        gold: GREATER_WOLF_GOLD + SMALL_WOLF_COUNT * SMALL_WOLF_GOLD,
        xp: Math.round(GREATER_WOLF_XP[lvl] + SMALL_WOLF_COUNT * SMALL_WOLF_XP[lvl]),
        cs: 2 + SMALL_WOLF_COUNT * 1, // 4
      };
    case 'krugs':
      return {
        gold: ANCIENT_KRUG_GOLD + KRUG_GOLD + MINI_KRUG_COUNT * MINI_KRUG_GOLD,
        xp: Math.round(ANCIENT_KRUG_XP[lvl] + KRUG_XP[lvl] + MINI_KRUG_COUNT * MINI_KRUG_XP[lvl]),
        cs: 2 + 0.5 + MINI_KRUG_COUNT * 0.25, // 4
      };
    case 'gromp':
      return { gold: GROMP_GOLD, xp: Math.round(GROMP_XP[lvl]), cs: 4 };
    case 'blue_buff':
      return { gold: BLUE_BUFF_GOLD, xp: Math.round(BLUE_BUFF_XP[lvl]), cs: 4 };
    case 'red_buff':
      return { gold: RED_BUFF_GOLD, xp: Math.round(RED_BUFF_XP[lvl]), cs: 4 };
    case 'scuttle':
      return { gold: SCUTTLE_GOLD, xp: Math.round(SCUTTLE_XP[lvl]), cs: 4 };
    default:
      return { gold: 0, xp: 0, cs: 0 };
  }
}

/** Extract camp type from a RiftSimulator camp ID like "red-blue_buff" → "blue_buff" */
function extractCampType(campId: string): string {
  // Handle neutral camps (scuttle-top, scuttle-bot, dragon, etc.)
  if (campId.startsWith('scuttle')) return 'scuttle';
  if (campId === 'dragon') return 'dragon';
  if (campId === 'baron') return 'baron';
  if (campId === 'void-grubs') return 'void_grubs';
  if (campId === 'rift-herald') return 'rift_herald';
  // "blue-wolves" or "red-blue_buff" → take everything after first dash
  const idx = campId.indexOf('-');
  return idx >= 0 ? campId.slice(idx + 1) : campId;
}
